/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { usePrefersReducedMotion } from '@anatoliygatt/use-prefers-reduced-motion';
import useResponsiveVideo from 'effects/responsiveVideo';
import useGuid from 'effects/useGuid';
import useInview from 'effects/inview';
import Play from 'components/Icons/Play/Play';
import Pause from 'components/Icons/Pause/Pause';

/**
 * This is the VideoBackground component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function VideoBackground({ className, srcSet, labels }) {
  const guid = useGuid('video-background');
  const prefersReducedMotion = usePrefersReducedMotion();
  const [ref, inview] = useInview({ threshold: [0.1, 0.5, 0.9] });

  const video = useResponsiveVideo(srcSet);
  const [play, setPlay] = useState(!prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) {
      setPlay(false);
    }
  }, [prefersReducedMotion]);

  useEffect(() => {
    function onKeydown(e) {
      if (e.key === 'Pause') {
        e.preventDefault();
        setPlay(play => !play);
      }
    }

    if (inview) {
      window.addEventListener('keydown', onKeydown);
    }

    if (ref.current) {
      if (inview && play) {
        ref.current.play();
      } else {
        ref.current.pause();
      }
    }

    return function cleanup() {
      window.removeEventListener('keydown', onKeydown);
    };
  }, [inview, play]);

  if (!video) return null;

  return (
    <div className={className}>
      <video id={guid} ref={ref} src={video.link} muted loop playsInline />
      <button
        aria-controls={guid}
        aria-label={play ? labels.pause : labels.play}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setPlay(play => !play);
        }}
      >
        {play ? <Pause title={labels.pause} /> : <Play title={labels.play} />}
      </button>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
VideoBackground.propTypes = {
  className: PropTypes.string,
  srcSet: PropTypes.array.isRequired,
  labels: PropTypes.shape({
    play: PropTypes.string,
    pause: PropTypes.string,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
VideoBackground.defaultProps = {
  className: 'video-background',
  srcSet: [],
  labels: {
    play: 'Play',
    pause: 'Pause',
  },
};

export default VideoBackground;
